import * as React from "react";
import { EcgAnalysisSetTest, FixedComment } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { useFixedCommentsState } from "../Core/FixedCommentsContext";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { FixedCommentSetTypeEnum } from "../misc/api/FixedComments/FixedCommentSetTypes";

export interface SelectableFixedComment extends FixedComment {
    selected: boolean;
}

export interface TestFixedCommentsState {
    id: string;
    testNumber: number;
    isVisible: boolean;
    comments: SelectableFixedComment[];
}

export interface SessionFixedCommentState {
    isVisible: boolean;
    comments: SelectableFixedComment[];
}

export interface FixedCommentsState {
    isPageValidated: boolean;
    areAvailableFixedCommentsLoaded?: boolean;
    areFixedCommentsLoaded?: boolean;
    sessionFixedComments: SessionFixedCommentState;
    testFixedComments: TestFixedCommentsState[];
    alertFixedComments: TestFixedCommentsState[];
}

const initialFixedCommentsState = {
    isPageValidated: true,
    areAvailableFixedCommentsLoaded: null,
    areFixedCommentsLoaded: null,
    sessionFixedComments: {
        isVisible: false,
        comments: [],
    },
    testFixedComments: [],
    alertFixedComments: [],
}

export enum FixedCommentsActionTypes {
    LoadSessionFixedComments = "LoadSessionFixedComments",
    LoadTestFixedComments = "LoadTestFixedComments",
    LoadAlertFixedComments = "LoadAlertFixedComments",
    ToggleSessionFixedCommentChecked = "ToggleSessionFixedCommentChecked",
    ToggleTestFixedCommentChecked = "ToggleTestFixedCommentChecked",
    ToggleTestFixedCommentsVisibility = "ToggleTestFixedCommentsVisibility",
    ToggleSessionFixedCommentsVisibility = "ToggleSessionFixedCommentsVisibility",
    ToggleAlertFixedCommentChecked = "ToggleAlertFixedCommentChecked",
    ToggleAlertFixedCommentsVisibility = "ToggleAlertFixedCommentsVisibility",
    LoadedBothAvailableFixedComments = "LoadedBothAvailableFixedComments",
    PageValidated = "PageValidated",
}

export class ToggleSessionFixedCommentCheckedAction {
    type: FixedCommentsActionTypes.ToggleSessionFixedCommentChecked;
    id: string;
    checked: boolean;

    static create(id: string, checked: boolean): ToggleSessionFixedCommentCheckedAction {
        return {
            type: FixedCommentsActionTypes.ToggleSessionFixedCommentChecked,
            id: id,
            checked: checked,
        };
    }
}

export class ToggleTestFixedCommentCheckedAction {
    type: FixedCommentsActionTypes.ToggleTestFixedCommentChecked;
    testId: string;
    commentId: string;
    checked: boolean;

    static create(testId: string, commentId: string, checked: boolean): ToggleTestFixedCommentCheckedAction {
        return {
            type: FixedCommentsActionTypes.ToggleTestFixedCommentChecked,
            testId: testId,
            commentId: commentId,
            checked: checked,
        };
    }
}

export class ToggleTestFixedCommentsVisibilityAction {
    type: FixedCommentsActionTypes.ToggleTestFixedCommentsVisibility;
    testId: string;

    static create(testId: string): ToggleTestFixedCommentsVisibilityAction {
        return {
            type: FixedCommentsActionTypes.ToggleTestFixedCommentsVisibility,
            testId: testId,
        };
    }
}

export class ToggleSessionFixedCommentsVisibilityAction {
    type: FixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility;

    static create(): ToggleSessionFixedCommentsVisibilityAction {
        return {
            type: FixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility,
        };
    }
}

export class LoadSessionFixedCommentsAction {
    type: FixedCommentsActionTypes.LoadSessionFixedComments;
    comments: FixedComment[];

    static create(comments: FixedComment[]): LoadSessionFixedCommentsAction {
        return {
            type: FixedCommentsActionTypes.LoadSessionFixedComments,
            comments: comments,
        };
    }
}

export class LoadTestFixedCommentsAction {
    type: FixedCommentsActionTypes.LoadTestFixedComments;
    comments: FixedComment[];

    static create(comments: FixedComment[]): LoadTestFixedCommentsAction {
        return {
            type: FixedCommentsActionTypes.LoadTestFixedComments,
            comments: comments,
        };
    }
}

export class LoadAlertFixedCommentsAction {
    type: FixedCommentsActionTypes.LoadAlertFixedComments;
    comments: FixedComment[];

    static create(comments: FixedComment[]): LoadAlertFixedCommentsAction {
        return {
            type: FixedCommentsActionTypes.LoadAlertFixedComments,
            comments: comments,
        };
    }
}

export class LoadedBothAvailableFixedCommentsAction {
    type: FixedCommentsActionTypes.LoadedBothAvailableFixedComments;

    static create(): LoadedBothAvailableFixedCommentsAction {
        return {
            type: FixedCommentsActionTypes.LoadedBothAvailableFixedComments,
        }
    }
}

export class ToggleAlertFixedCommentCheckedAction {
    type: FixedCommentsActionTypes.ToggleAlertFixedCommentChecked;
    testId: string;
    commentId: string;
    checked: boolean;

    static create(testId: string, commentId: string, checked: boolean): ToggleAlertFixedCommentCheckedAction {
        return {
            type: FixedCommentsActionTypes.ToggleAlertFixedCommentChecked,
            testId: testId,
            commentId: commentId,
            checked: checked,
        };
    }
}

export class ToggleAlertFixedCommentsVisibilityAction {
    type: FixedCommentsActionTypes.ToggleAlertFixedCommentsVisibility;
    testId: string;

    static create(testId: string): ToggleAlertFixedCommentsVisibilityAction {
        return {
            type: FixedCommentsActionTypes.ToggleAlertFixedCommentsVisibility,
            testId: testId,
        };
    }
}

export class PageValidatedAction {
    type: FixedCommentsActionTypes.PageValidated;
    isValidated: boolean;

    static create(isValidated: boolean): PageValidatedAction {
        return {
            type: FixedCommentsActionTypes.PageValidated,
            isValidated: isValidated,
        };
    }
}

type Actions = LoadSessionFixedCommentsAction | LoadTestFixedCommentsAction | LoadAlertFixedCommentsAction | ToggleSessionFixedCommentCheckedAction | ToggleTestFixedCommentCheckedAction | ToggleSessionFixedCommentsVisibilityAction | ToggleTestFixedCommentsVisibilityAction | LoadedBothAvailableFixedCommentsAction | PageValidatedAction | ToggleAlertFixedCommentCheckedAction | ToggleAlertFixedCommentsVisibilityAction;

export const useFixedComments = (tests: EcgAnalysisSetTest[], sessionTypeId: SessionTypes): [FixedCommentsState, React.Dispatch<any>] => {
    const globalFixedCommentsState = useFixedCommentsState();
    const { study } = useCurrentStudyConfigurationContext();
    const sessionFixedComments = globalFixedCommentsState.session.get(`${sessionTypeId}-${FixedCommentSetTypeEnum.Overread}`);
    const testFixedComments = globalFixedCommentsState.test.get(`${sessionTypeId}-${FixedCommentSetTypeEnum.Overread}`);
    const alertFixedComments = globalFixedCommentsState.alerts.get(sessionTypeId);

    const reducer = (state: FixedCommentsState, action: Actions): FixedCommentsState => {
        switch (action.type) {
            case FixedCommentsActionTypes.LoadSessionFixedComments:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        comments: action.comments.map(c => ({
                            ...c,
                            selected: false,
                        })),
                    },
                };
            case FixedCommentsActionTypes.LoadTestFixedComments:
                const testFixedComments = tests.map(tr => ({
                    id: tr.testId,
                    testNumber: tr.test.index,
                    isVisible: false,
                    comments: action.comments.map(c => ({
                        ...c,
                        selected: false,
                    })),
                }));

                return {
                    ...state,
                    testFixedComments: testFixedComments,
                };
            case FixedCommentsActionTypes.LoadAlertFixedComments:
                const alertFixedComments = tests.map(tr => ({
                    id: tr.testId,
                    testNumber: tr.test.index,
                    isVisible: false,
                    comments: action.comments.map(c => ({
                        ...c,
                        selected: false,
                    })),
                }));

                return {
                    ...state,
                    alertFixedComments: alertFixedComments,
                };
            case FixedCommentsActionTypes.ToggleSessionFixedCommentChecked:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        comments: state.sessionFixedComments.comments.map(c => {
                            if (c.id === action.id) {
                                return {
                                    ...c,
                                    selected: !c.selected,
                                };
                            } else {
                                return c;
                            }
                        })
                    }
                };
            case FixedCommentsActionTypes.ToggleTestFixedCommentChecked:
                return {
                    ...state,
                    testFixedComments: state.testFixedComments.map(tfc => {
                        if (tfc.id === action.testId) {
                            return {
                                ...tfc,
                                comments: tfc.comments.map(c => {
                                    if (c.id === action.commentId) {
                                        return {
                                            ...c,
                                            selected: !c.selected,
                                        };
                                    } else {
                                        return c;
                                    }
                                })
                            }
                        } else {
                            return tfc;
                        }
                    }),
                };
            case FixedCommentsActionTypes.ToggleAlertFixedCommentChecked:
                return {
                    ...state,
                    alertFixedComments: state.alertFixedComments.map(tfc => {
                        if (tfc.id === action.testId) {
                            return {
                                ...tfc,
                                comments: tfc.comments.map(c => {
                                    if (c.id === action.commentId) {
                                        return {
                                            ...c,
                                            selected: !c.selected,
                                        };
                                    } else {
                                        return c;
                                    }
                                })
                            }
                        } else {
                            return tfc;
                        }
                    }),
                };
            case FixedCommentsActionTypes.ToggleTestFixedCommentsVisibility:
                return {
                    ...state,
                    testFixedComments: state.testFixedComments.map(t => {
                        if (t.id === action.testId) {
                            return {
                                ...t,
                                isVisible: !t.isVisible,
                            };
                        }
                        else return t;
                    }
                    ),
                }
            case FixedCommentsActionTypes.ToggleAlertFixedCommentsVisibility:
                return {
                    ...state,
                    alertFixedComments: state.alertFixedComments.map(t => {
                        if (t.id === action.testId) {
                            return {
                                ...t,
                                isVisible: !t.isVisible,
                            };
                        }
                        else return t;
                    }
                    ),
                }
            case FixedCommentsActionTypes.ToggleSessionFixedCommentsVisibility:
                return {
                    ...state,
                    sessionFixedComments: {
                        ...state.sessionFixedComments,
                        isVisible: !state.sessionFixedComments.isVisible,
                    },
                };
            case FixedCommentsActionTypes.PageValidated:
                return {
                    ...state,
                    isPageValidated: action.isValidated,
                };
            case FixedCommentsActionTypes.LoadedBothAvailableFixedComments:
                return {
                    ...state,
                    areAvailableFixedCommentsLoaded: true,
                }
            default:
                throw new Error("Unexpected Fixed Comments action");
        }
    }

    const [state, dispatch] = React.useReducer(reducer, initialFixedCommentsState);

    React.useEffect(() => {
        const fetchComments = async () => {
            if (sessionFixedComments) {
                dispatch(LoadSessionFixedCommentsAction.create(sessionFixedComments.filter(sfc => !sfc.disabled)));
            }

            if (testFixedComments) {
                dispatch(LoadTestFixedCommentsAction.create(testFixedComments.filter(tfc => !tfc.disabled)));
            }

            if (alertFixedComments) {
                dispatch(LoadAlertFixedCommentsAction.create(alertFixedComments.filter(tfc => !tfc.disabled)));
            }

            dispatch(LoadedBothAvailableFixedCommentsAction.create());
        };

        if (tests !== undefined && tests.length > 0) {
            fetchComments();
        }
    }, [study.id, dispatch, tests.length, sessionTypeId])

    return [state, dispatch];
}


